/**
 * Terms & Claims — the plan cycle's stopping rules and resource declarations.
 *
 * The moment (canon 02B §5): "A review date arrived, or someone asks whether
 * we're really resourcing what we agreed — which commitments must justify
 * themselves, and did the resources follow?"
 *
 * PRD: docs/9-exploration/PRD-scorecard-to-strongest.md (WS-A, WS-B1).
 * Data: commitment_terms_check + resource_claims_followthrough (migs 680/681),
 * writes via ceremony RPCs only (§2.3a). Judgment: src/lib/commitmentTerms.ts.
 *
 * L2: every state change here is a named human clicking a button and stating
 * a basis. Nothing trips, stops, or expires on its own — an armed term past
 * its date renders as "Past review date" until a person decides.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';
import { EntityListPage } from '../../components/collection';
import type { SortOption } from '../../components/collection/types';
import {
  PlanningCycleScope,
  type PlanningCycleContext,
} from '../../components/planning/PlanningCycleScope';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { ReverseActionButton } from '../../components/reversals/ReverseActionButton';
import { unplacePlanItem } from '../../lib/reversals/reversalsService';
import { CommitmentCell } from '../../components/planning/CommitmentCell';
import { useCommitmentViewEnabled } from '../../hooks/useCommitmentViewEnabled';
import {
  attachTerm,
  listTermsForCycle,
  resolveTerm,
  tripTerm,
  withdrawTerm,
  type CommitmentTermCheckRow,
} from '../../lib/commitmentTermsService';
import {
  declareClaim,
  listFollowThroughForCycle,
  withdrawClaim,
  type ResourceClaimFollowThroughRow,
} from '../../lib/resourceClaimsService';
import {
  placePlanItem,
  listPlacementsForCycle,
  listSequenceCheckForCycle,
  type PlanItemPlacementRow,
  type PlanSequenceCheckRow,
} from '../../lib/planSequenceService';
import {
  listReallocationDeltaForCycle,
  type PlanCycleReallocationRow,
} from '../../lib/planDeltaService';
import { Link } from 'react-router-dom';
import {
  TERM_KIND_LABELS,
  describeTermStatus,
  isTermOpen,
  judgeFollowThrough,
  type TermKind,
  type TermResolution,
} from '../../lib/commitmentTerms';
import { listTrippedTermSignals } from '../../lib/signalEventsService';
import {
  cycleHasDisplacementDiscipline,
  displacementAnsweredForItem,
  displacementCeremonyHref,
} from '../../lib/displacementService';
import {
  DISPLACEMENT_ALREADY_ANSWERED,
  DISPLACEMENT_ANSWER_LINK_LABEL,
  DISPLACEMENT_RULE_PLACE_NOTICE,
} from '../../lib/planning/vocabulary';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

const TERM_SORT_OPTIONS: SortOption<string>[] = [
  { field: 'review_by', label: 'Review by' },
  { field: 'created_at', label: 'Attached' },
  { field: 'status', label: 'Status' },
];

const CLAIM_SORT_OPTIONS: SortOption<string>[] = [
  { field: 'created_at', label: 'Declared' },
  { field: 'claim_kind', label: 'Kind' },
];

const SEQUENCE_SORT_OPTIONS: SortOption<string>[] = [
  { field: 'periodIndex', label: 'Period' },
  { field: 'title', label: 'Commitment' },
];

const REALLOC_SORT_OPTIONS: SortOption<string>[] = [
  { field: 'change', label: 'Change' },
];

interface SequenceRow {
  itemId: string;
  title: string;
  periodIndex: number | null;
  periodLabel: string | null;
  problems: string[];
}

type ItemTitleMap = Map<string, string>;

function itemLabel(itemId: string | null, titles: ItemTitleMap): string {
  if (!itemId) return '—';
  return titles.get(itemId) ?? `Item ${itemId.slice(0, 8)}… (not in the current version)`;
}

/**
 * Loads the cycle's version numbering and its active authorization (P4).
 *
 * Kept as a plain function rather than a hook so it can be awaited inside the
 * page's existing single reload path — one more await, no new render cycle.
 */
interface VersionContext {
  versionNumbers: Map<string, number>;
  authorizedVersion: number | null;
  /** The cycle's own workspace — what the §2.7 gate must resolve against. */
  workspaceId: string | null;
}

async function loadVersionContext(cycleId: string): Promise<VersionContext> {
  const db = getTypedSupabaseClient();
  const [{ data: versions }, { data: cycle }, { data: snapshot }] = await Promise.all([
    db.from('plan_versions').select('id, version_number').eq('plan_cycle_id', cycleId),
    db.from('plan_cycles').select('workspace_id').eq('id', cycleId).maybeSingle(),
    db
      .from('plan_of_record_snapshots')
      .select('plan_version_id')
      .eq('plan_cycle_id', cycleId)
      .is('superseded_by_snapshot_id', null)
      .maybeSingle(),
  ]);

  const versionNumbers = new Map<string, number>(
    (versions ?? []).map((v) => [v.id, v.version_number])
  );
  return {
    versionNumbers,
    authorizedVersion: snapshot?.plan_version_id
      ? (versionNumbers.get(snapshot.plan_version_id) ?? null)
      : null,
    workspaceId: cycle?.workspace_id ?? null,
  };
}

// ============================================================================
// Modal shell (Pattern A)
// ============================================================================

function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md border border-[var(--rule)]/80 bg-[var(--bg-raised)] p-6 shadow-2xl dark:border-[var(--rule)] dark:bg-[var(--surface)]">
        <h2 className="mb-4 font-heading text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

const inputClass =
  'w-full  border border-[var(--rule)] px-3 py-2 text-sm focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]';
const labelClass =
  'mb-1 block text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)]';
const ctaClass =
  ' bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-50';
const cancelClass =
  ' border border-[var(--rule)] px-4 py-2 text-sm text-[var(--text-sec)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]';

function ModalError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p className="mb-3 bg-[var(--coral-dim)] px-3 py-2 text-sm text-[var(--coral)] dark:bg-[var(--surface-raised)] dark:text-[var(--coral)]">
      {message}
    </p>
  );
}

// ============================================================================
// Attach term modal
// ============================================================================

function AttachTermModal({
  cycleId,
  items,
  onClose,
  onDone,
}: {
  cycleId: string;
  items: Array<{ itemId: string; title: string }>;
  onClose: () => void;
  onDone: () => void;
}) {
  const [itemId, setItemId] = useState(items[0]?.itemId ?? '');
  const [kind, setKind] = useState<TermKind>('review_trigger');
  const [condition, setCondition] = useState('');
  const [reviewBy, setReviewBy] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result = await attachTerm({
      planCycleId: cycleId,
      itemId,
      kind,
      conditionText: condition,
      reviewBy: reviewBy || undefined,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The term could not be attached.');
      return;
    }
    onDone();
  };

  return (
    <ModalShell title="Attach a term" onClose={onClose}>
      <ModalError message={error} />
      <div className="space-y-3">
        <div>
          <label className={labelClass}>Commitment</label>
          <select className={inputClass} value={itemId} onChange={(e) => setItemId(e.target.value)}>
            {items.map((i) => (
              <option key={i.itemId} value={i.itemId}>
                {i.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Kind</label>
          <select
            className={inputClass}
            value={kind}
            onChange={(e) => setKind(e.target.value as TermKind)}
          >
            <option value="review_trigger">Review trigger — a condition that forces a decision</option>
            <option value="kill_criterion">Kill criterion — a condition under which this stops</option>
            <option value="expiry">Expiry — must justify itself by a date</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Condition (plain language)</label>
          <textarea
            className={inputClass}
            rows={2}
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            placeholder="e.g. Fewer than 3 teams adopted by the review date"
          />
        </div>
        <div>
          <label className={labelClass}>
            Review by {kind === 'expiry' ? '(required)' : '(optional)'}
          </label>
          <input
            type="date"
            className={inputClass}
            value={reviewBy}
            onChange={(e) => setReviewBy(e.target.value)}
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={cancelClass} onClick={onClose}>
          Cancel
        </button>
        <button
          className={ctaClass}
          disabled={busy || !itemId || !condition.trim() || (kind === 'expiry' && !reviewBy)}
          onClick={submit}
        >
          Attach term
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Term action modal (trip / resolve / withdraw — basis always required)
// ============================================================================

type TermAction = 'trip' | 'resolve' | 'withdraw';

function TermActionModal({
  term,
  action,
  planCycleId,
  onClose,
  onDone,
}: {
  term: CommitmentTermCheckRow;
  action: TermAction;
  /** Threaded to the post-trip "view the signal" link. */
  planCycleId: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [basis, setBasis] = useState('');
  const [resolution, setResolution] = useState<TermResolution>('stop');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Break 3: a trip enters the signal log (best-effort, mig 682). Show the
  // link before closing rather than closing silently.
  const [tripped, setTripped] = useState(false);

  const titles: Record<TermAction, string> = {
    trip: 'Trip this term',
    resolve: 'Resolve this term',
    withdraw: 'Withdraw this term',
  };

  const submit = async () => {
    if (!term.id) return;
    setBusy(true);
    setError(null);
    const result =
      action === 'trip'
        ? await tripTerm(term.id, basis)
        : action === 'resolve'
          ? await resolveTerm(term.id, resolution, basis)
          : await withdrawTerm(term.id, basis);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The change was not recorded.');
      return;
    }
    if (action === 'trip') {
      setTripped(true);
      return;
    }
    onDone();
  };

  if (tripped) {
    return (
      <ModalShell title={titles[action]} onClose={onDone}>
        <div className="border border-[var(--green)] bg-[var(--green-dim)]/40 px-4 py-3 dark:border-[var(--rule)] dark:bg-[var(--surface-raised)]">
          <p className="text-sm font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
            Term tripped.
          </p>
          <p className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
            A signal was recorded —{' '}
            <Link
              to={`/planning/plans/${planCycleId}`}
              className="text-primary-700 hover:underline dark:text-[var(--text-sec)]"
              onClick={onDone}
            >
              view it
            </Link>
            .
          </p>
        </div>
        <div className="mt-5 flex justify-end">
          <button className={ctaClass} onClick={onDone}>
            Done
          </button>
        </div>
      </ModalShell>
    );
  }

  return (
    <ModalShell title={titles[action]} onClose={onClose}>
      <ModalError message={error} />
      <p className="mb-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        {term.condition_text}
      </p>
      {action === 'resolve' && (
        <div className="mb-3">
          <label className={labelClass}>Decision</label>
          <select
            className={inputClass}
            value={resolution}
            onChange={(e) => setResolution(e.target.value as TermResolution)}
          >
            <option value="stop">Stop — the commitment ends and joins the cut</option>
            <option value="rescope">Rescope — the commitment continues, reduced</option>
            <option value="keep">Keep — the commitment continues as agreed</option>
          </select>
        </div>
      )}
      <div>
        <label className={labelClass}>Basis (required — this goes on the record)</label>
        <textarea
          className={inputClass}
          rows={2}
          value={basis}
          onChange={(e) => setBasis(e.target.value)}
          placeholder={
            action === 'trip' ? 'What happened?' : 'Why is this the call?'
          }
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={cancelClass} onClick={onClose}>
          Cancel
        </button>
        <button className={ctaClass} disabled={busy || !basis.trim()} onClick={submit}>
          Record it
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Withdraw claim modal (basis always required — same pattern as term actions)
// ============================================================================

function WithdrawClaimModal({
  claim,
  onClose,
  onDone,
}: {
  claim: ResourceClaimFollowThroughRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!claim.id || !basis.trim()) return;
    setBusy(true);
    setError(null);
    const result = await withdrawClaim(claim.id, basis);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Not recorded.');
      return;
    }
    onDone();
  };

  return (
    <ModalShell title="Withdraw this claim" onClose={onClose}>
      <ModalError message={error} />
      <div>
        <label className={labelClass}>Basis (required — this goes on the record)</label>
        <textarea
          className={inputClass}
          rows={2}
          value={basis}
          onChange={(e) => setBasis(e.target.value)}
          placeholder="Why is this claim being withdrawn?"
        />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={cancelClass} onClick={onClose}>
          Cancel
        </button>
        <button className={ctaClass} disabled={busy || !basis.trim()} onClick={submit}>
          Record it
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Declare claim modal
// ============================================================================

function DeclareClaimModal({
  cycleId,
  items,
  supersedes,
  onClose,
  onDone,
}: {
  cycleId: string;
  items: Array<{ itemId: string; title: string }>;
  supersedes?: ResourceClaimFollowThroughRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [itemId, setItemId] = useState(supersedes?.item_id ?? items[0]?.itemId ?? '');
  const [kind, setKind] = useState<'capacity_hours' | 'funding'>(
    (supersedes?.claim_kind as 'capacity_hours' | 'funding') ?? 'capacity_hours'
  );
  const [hours, setHours] = useState('');
  const [amount, setAmount] = useState('');
  const [resourceType, setResourceType] = useState(supersedes?.resource_type ?? '');
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result = await declareClaim({
      planCycleId: cycleId,
      itemId,
      claimKind: kind,
      declaredHours: kind === 'capacity_hours' ? Number(hours) : undefined,
      declaredAmount: kind === 'funding' ? Number(amount) : undefined,
      resourceType: resourceType || undefined,
      basis: basis || undefined,
      supersedesClaimId: supersedes?.id ?? undefined,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The claim could not be declared.');
      return;
    }
    onDone();
  };

  const valueValid =
    kind === 'capacity_hours' ? Number(hours) > 0 : Number(amount) > 0;

  return (
    <ModalShell
      title={supersedes ? 'Declare a superseding claim' : 'Declare a resource claim'}
      onClose={onClose}
    >
      <ModalError message={error} />
      {supersedes && (
        <p className="mb-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          The current claim stays on the record and is superseded by this one — claims
          change by supersession, never by edit.
        </p>
      )}
      <div className="space-y-3">
        <div>
          <label className={labelClass}>Commitment</label>
          <select
            className={inputClass}
            value={itemId}
            disabled={!!supersedes}
            onChange={(e) => setItemId(e.target.value)}
          >
            {items.map((i) => (
              <option key={i.itemId} value={i.itemId}>
                {i.title}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>What is being committed</label>
          <select
            className={inputClass}
            value={kind}
            disabled={!!supersedes}
            onChange={(e) => setKind(e.target.value as 'capacity_hours' | 'funding')}
          >
            <option value="capacity_hours">Capacity (hours)</option>
            <option value="funding">Funding (amount)</option>
          </select>
        </div>
        {kind === 'capacity_hours' ? (
          <div>
            <label className={labelClass}>Declared hours</label>
            <input
              type="number"
              min="1"
              className={inputClass}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
            />
          </div>
        ) : (
          <div>
            <label className={labelClass}>Declared amount (USD)</label>
            <input
              type="number"
              min="1"
              className={inputClass}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
        )}
        <div>
          <label className={labelClass}>Resource (optional)</label>
          <input
            className={inputClass}
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            placeholder="e.g. engineering"
          />
        </div>
        <div>
          <label className={labelClass}>Basis (optional)</label>
          <input
            className={inputClass}
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder="Why this amount"
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={cancelClass} onClick={onClose}>
          Cancel
        </button>
        <button className={ctaClass} disabled={busy || !itemId || !valueValid} onClick={submit}>
          Declare claim
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Place item modal (WS-C)
// ============================================================================

function PlaceItemModal({
  cycleId,
  items,
  initialItemId,
  onClose,
  onDone,
}: {
  cycleId: string;
  items: Array<{ itemId: string; title: string }>;
  initialItemId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [itemId, setItemId] = useState(initialItemId ?? items[0]?.itemId ?? '');
  const [periodIndex, setPeriodIndex] = useState('');
  const [periodLabel, setPeriodLabel] = useState('');
  const [basis, setBasis] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Owner ruling #12 (769): in a plan cycle whose template adopted the rule,
  // adding work means answering "what comes off?" first. The rule is the
  // cycle's (seeded from its template at creation); the ceremony itself lives
  // on Above / Below the Line, where the engine's numbers are.
  const [disciplineOn, setDisciplineOn] = useState(false);
  const [answered, setAnswered] = useState(false);
  useEffect(() => {
    let cancelled = false;
    void cycleHasDisplacementDiscipline(cycleId).then((on) => {
      if (!cancelled) setDisciplineOn(on);
    });
    return () => { cancelled = true; };
  }, [cycleId]);
  useEffect(() => {
    let cancelled = false;
    setAnswered(false);
    if (!disciplineOn || !itemId) return;
    void displacementAnsweredForItem(cycleId, itemId).then((done) => {
      if (!cancelled) setAnswered(done);
    });
    return () => { cancelled = true; };
  }, [cycleId, itemId, disciplineOn]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    const result = await placePlanItem({
      planCycleId: cycleId,
      itemId,
      periodIndex: Number(periodIndex),
      periodLabel: periodLabel || undefined,
      basis: basis || undefined,
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'The placement was not recorded.');
      return;
    }
    onDone();
  };

  return (
    <ModalShell title="Place a commitment" onClose={onClose}>
      <ModalError message={error} />
      <p className="mb-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
        Re-placing supersedes the previous placement — the change stays on the record.
      </p>
      {disciplineOn && itemId && (
        <div className="mb-3 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-3 py-2 text-sm text-[var(--text-pri)]">
          {answered ? (
            DISPLACEMENT_ALREADY_ANSWERED
          ) : (
            <>
              {DISPLACEMENT_RULE_PLACE_NOTICE}{' '}
              <Link
                to={displacementCeremonyHref(cycleId, itemId)}
                className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                {DISPLACEMENT_ANSWER_LINK_LABEL} →
              </Link>
            </>
          )}
        </div>
      )}
      <div className="space-y-3">
        <div>
          <label className={labelClass}>Commitment</label>
          <select className={inputClass} value={itemId} onChange={(e) => setItemId(e.target.value)}>
            {items.map((i) => (
              <option key={i.itemId} value={i.itemId}>
                {i.title}
              </option>
            ))}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Period (0 = first)</label>
            <input
              type="number"
              min="0"
              className={inputClass}
              value={periodIndex}
              onChange={(e) => setPeriodIndex(e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>Label (optional)</label>
            <input
              className={inputClass}
              value={periodLabel}
              onChange={(e) => setPeriodLabel(e.target.value)}
              placeholder="e.g. Q4"
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Basis (optional)</label>
          <input
            className={inputClass}
            value={basis}
            onChange={(e) => setBasis(e.target.value)}
            placeholder="Why this period"
          />
        </div>
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <button className={cancelClass} onClick={onClose}>
          Cancel
        </button>
        <button
          className={ctaClass}
          disabled={busy || !itemId || periodIndex === '' || Number(periodIndex) < 0}
          onClick={submit}
        >
          Place it
        </button>
      </div>
    </ModalShell>
  );
}

// ============================================================================
// Status chips
// ============================================================================

function TermStatusChip({ term, today }: { term: CommitmentTermCheckRow; today: string }) {
  const view = describeTermStatus(
    {
      status: (term.status ?? 'armed') as 'armed' | 'tripped' | 'resolved' | 'withdrawn',
      resolution: term.resolution as TermResolution | null,
      reviewBy: term.review_by,
      trippedBasis: term.tripped_basis,
      resolutionBasis: term.resolution_basis,
      withdrawnBasis: term.withdrawn_basis,
    },
    today
  );
  const tone = view.needsDecision
    ? 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--surface-raised)] dark:text-[var(--mustard)]'
    : 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]';
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`} title={view.sentence}>
      {view.label}
    </span>
  );
}

// ============================================================================
// Page content
// ============================================================================

type ViewKey = 'terms' | 'claims' | 'sequence' | 'reallocation';

function TermsClaimsContent({ ctx }: { ctx: PlanningCycleContext }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view');
  const view: ViewKey =
    viewParam === 'claims' || viewParam === 'sequence' || viewParam === 'reallocation'
      ? viewParam
      : 'terms';
  const today = todayIso();

  const [terms, setTerms] = useState<CommitmentTermCheckRow[]>([]);
  // Break 3: a tripped term is a signal (mig 682, best-effort insert) — this
  // maps term id → the signal it produced, so the row can say so and link to
  // the plan's signal log instead of tripping silently.
  const [trippedSignals, setTrippedSignals] = useState<Map<string, { id: string }>>(new Map());
  const [claims, setClaims] = useState<ResourceClaimFollowThroughRow[]>([]);
  const [placements, setPlacements] = useState<PlanItemPlacementRow[]>([]);
  const [seqChecks, setSeqChecks] = useState<PlanSequenceCheckRow[]>([]);
  const [realloc, setRealloc] = useState<PlanCycleReallocationRow[]>([]);
  const [itemTitles, setItemTitles] = useState<ItemTitleMap>(new Map());
  /**
   * P4: plan-version id → version number, plus the cycle's authorized version.
   * Terms and claims each record the plan version they were written against
   * (`created_in_plan_version_id` / `declared_in_plan_version_id`), so a row
   * written before the current authorization is quoting an older reading of
   * the commitment — which is exactly what the marker exists to surface.
   */
  const [versionNumbers, setVersionNumbers] = useState<Map<string, number>>(new Map());
  const [authorizedVersion, setAuthorizedVersion] = useState<number | null>(null);
  const [cycleWorkspaceId, setCycleWorkspaceId] = useState<string | null>(null);
  // P3/P4 gate, resolved against the plan cycle's own workspace (§2.7) — a plan
  // reached by URL can belong to a workspace other than the session's.
  const commitmentViewOn = useCommitmentViewEnabled(cycleWorkspaceId);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [modal, setModal] = useState<
    | { kind: 'attach' }
    | { kind: 'term-action'; term: CommitmentTermCheckRow; action: TermAction }
    | { kind: 'declare'; supersedes?: ResourceClaimFollowThroughRow }
    | { kind: 'place'; itemId?: string }
    | { kind: 'withdraw-claim'; claim: ResourceClaimFollowThroughRow }
    | null
  >(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [termsRes, claimsRes, placementsRes, seqRes, reallocRes] = await Promise.all([
      listTermsForCycle(ctx.cycleId),
      listFollowThroughForCycle(ctx.cycleId),
      listPlacementsForCycle(ctx.cycleId),
      listSequenceCheckForCycle(ctx.cycleId),
      listReallocationDeltaForCycle(ctx.cycleId),
    ]);
    setTerms(termsRes.rows);
    setClaims(claimsRes.rows);
    if (termsRes.rows.some((t) => t.status === 'tripped')) {
      void listTrippedTermSignals(ctx.cycleId).then(setTrippedSignals);
    } else {
      setTrippedSignals(new Map());
    }
    setPlacements(placementsRes.rows);
    setSeqChecks(seqRes.rows);
    setRealloc(reallocRes.rows);
    setLoadError(termsRes.error ?? claimsRes.error ?? null);

    if (ctx.planVersionId) {
      const db = getTypedSupabaseClient();
      const { data } = await db
        .from('initiatives')
        .select('item_id, title')
        .eq('plan_version_id', ctx.planVersionId);
      const map: ItemTitleMap = new Map();
      for (const row of data ?? []) {
        if (row.item_id) map.set(row.item_id, row.title ?? row.item_id);
      }
      setItemTitles(map);
    }

    const versionContext = await loadVersionContext(ctx.cycleId);
    setVersionNumbers(versionContext.versionNumbers);
    setAuthorizedVersion(versionContext.authorizedVersion);
    setCycleWorkspaceId(versionContext.workspaceId);
    setLoading(false);
  }, [ctx.cycleId, ctx.planVersionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const currentItems = useMemo(
    () =>
      Array.from(itemTitles.entries())
        .map(([itemId, title]) => ({ itemId, title }))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [itemTitles]
  );

  const closeAndReload = () => {
    setModal(null);
    void reload();
  };

  const switchView = (next: ViewKey) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'terms') params.delete('view');
    else params.set('view', next);
    setSearchParams(params, { replace: true });
  };

  const sequenceRows: SequenceRow[] = useMemo(() => {
    const placedByItem = new Map(placements.map((pl) => [pl.item_id, pl]));
    const problemsByItem = new Map<string, string[]>();
    for (const chk of seqChecks) {
      if (chk.check_state === 'ok' || !chk.dependent_item_id) continue;
      const sentence =
        chk.check_state === 'before_dependency'
          ? `Placed before "${chk.prerequisite_title ?? 'its prerequisite'}" (period ${chk.prerequisite_period ?? '?'})`
          : chk.check_state === 'prerequisite_unplaced'
            ? `Depends on "${chk.prerequisite_title ?? 'an item'}" that has no placement yet`
            : 'Not yet placed, but has dependencies';
      const list = problemsByItem.get(chk.dependent_item_id) ?? [];
      list.push(sentence);
      problemsByItem.set(chk.dependent_item_id, list);
    }
    return currentItems.map((i) => ({
      itemId: i.itemId,
      title: i.title,
      periodIndex: placedByItem.get(i.itemId)?.period_index ?? null,
      periodLabel: placedByItem.get(i.itemId)?.period_label ?? null,
      problems: problemsByItem.get(i.itemId) ?? [],
    }));
  }, [currentItems, placements, seqChecks]);

  const viewToggle = (
    <div className="flex items-center gap-1 border border-[var(--rule)] p-0.5 dark:border-[var(--rule)]">
      {(['terms', 'claims', 'sequence', 'reallocation'] as const).map((key) => (
        <button
          key={key}
          onClick={() => switchView(key)}
          className={`rounded px-2.5 py-1 text-xs font-medium ${
            view === key
              ? 'bg-primary-50 text-primary-700 dark:bg-[var(--active-tint)] dark:text-[var(--text-pri)]'
              : 'text-[var(--text-sec)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
          }`}
        >
          {key === 'terms'
            ? `Terms (${terms.length})`
            : key === 'claims'
              ? `Claims (${claims.length})`
              : key === 'sequence'
                ? `Sequence (${placements.length})`
                : `Reallocation (${realloc.length})`}
        </button>
      ))}
    </div>
  );

  if (view === 'sequence') {
    const inversions = sequenceRows.filter((r) => r.problems.length > 0).length;
    return (
      <>
        <EntityListPage<SequenceRow, string>
          items={sequenceRows}
          loading={loading}
          getId={(r) => r.itemId}
          masterDetail={false}
          title="Terms & Claims"
          subtitle="When each commitment happens — placed by a person, checked against dependencies"
          icon={<ClipboardCheck className="h-4 w-4 text-primary-600" />}
          iconBgClassName="bg-primary-50"
          itemLabel="commitment"
          searchFields={['title']}
          searchPlaceholder="Search commitments…"
          sortOptions={SEQUENCE_SORT_OPTIONS}
          defaultSort={{ field: 'periodIndex', direction: 'asc' }}
          viewModes={['table']}
          defaultViewMode="table"
          selectable={false}
          primaryAction={{
            label: 'Place item',
            onClick: () => setModal({ kind: 'place' }),
            disabled: currentItems.length === 0,
          }}
          headerActions={viewToggle}
          tableColumns={[
            {
              key: 'title',
              label: 'Commitment',
              sortable: true,
              minWidth: 220,
              render: (r) => (
                <span className="text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{r.title}</span>
              ),
            },
            {
              key: 'periodIndex',
              label: 'Period',
              sortable: true,
              minWidth: 110,
              render: (r) => (
                <span className="tabular-nums text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                  {r.periodIndex == null
                    ? '—'
                    : r.periodLabel
                      ? `${r.periodIndex} · ${r.periodLabel}`
                      : String(r.periodIndex)}
                </span>
              ),
            },
            {
              key: 'order',
              label: 'Order check',
              minWidth: 280,
              render: (r) =>
                r.problems.length > 0 ? (
                  <span className="text-xs font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                    {r.problems.join(' · ')}
                  </span>
                ) : r.periodIndex == null ? (
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">Not placed yet</span>
                ) : (
                  <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">In order</span>
                ),
            },
            {
              key: 'actions',
              label: '',
              minWidth: 160,
              render: (r) => (
                <div className="flex items-center gap-2">
                  <button
                    className="text-xs text-primary-600 hover:underline"
                    onClick={() => setModal({ kind: 'place', itemId: r.itemId })}
                  >
                    {r.periodIndex == null ? 'Place' : 'Re-place'}
                  </button>
                  {r.periodIndex != null && (
                    <ReverseActionButton
                      label="Remove placement"
                      confirmTitle="Remove placement"
                      explain={`This will un-place "${r.title}" from period ${r.periodIndex}${r.periodLabel ? ` (${r.periodLabel})` : ''}, restoring the placement it replaced (if any).`}
                      run={(reason) => unplacePlanItem(ctx.cycleId, r.itemId, reason)}
                      onDone={() => void reload()}
                      variant="ghost"
                      size="sm"
                      className="!px-2 !py-1 !text-xs"
                    />
                  )}
                </div>
              ),
            },
          ]}
          emptyTitle="No commitments to place"
          emptyDescription="Commitments appear here once the plan cycle has a version with items on it."
          contextBar={{
            label:
              loadError ??
              (inversions > 0
                ? `${inversions} commitment${inversions !== 1 ? 's' : ''} out of order`
                : `Plan cycle · ${placements.length} placed`),
          }}
          shellClassName="h-full"
        />
        {modal?.kind === 'place' && (
          <PlaceItemModal
            cycleId={ctx.cycleId}
            items={currentItems}
            initialItemId={modal.itemId}
            onClose={() => setModal(null)}
            onDone={closeAndReload}
          />
        )}
      </>
    );
  }

  if (view === 'reallocation') {
    return (
      <EntityListPage<PlanCycleReallocationRow, string>
        items={realloc}
        loading={loading}
        getId={(r) => `${r.item_id}`}
        masterDetail={false}
        title="Terms & Claims"
        subtitle="What actually moved since the previous cycle — and what rolled over untouched"
        icon={<ClipboardCheck className="h-4 w-4 text-primary-600" />}
        iconBgClassName="bg-primary-50"
        itemLabel="movement"
        searchFields={[]}
        searchPlaceholder="Search…"
        sortOptions={REALLOC_SORT_OPTIONS}
        defaultSort={{ field: 'change', direction: 'asc' }}
        viewModes={['table']}
        defaultViewMode="table"
        selectable={false}
        headerActions={viewToggle}
        tableColumns={[
          {
            key: 'item',
            label: 'Commitment',
            minWidth: 220,
            render: (r) => (
              <CommitmentCell
                planCycleId={ctx.cycleId}
                itemId={r.item_id}
                label={itemLabel(r.item_id, itemTitles)}
                linkEnabled={commitmentViewOn}
              />
            ),
          },
          {
            key: 'previous',
            label: 'Previous cycle',
            minWidth: 130,
            render: (r) => (
              <span className="tabular-nums text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {r.previous_hours != null ? `${r.previous_hours} h` : ''}
                {r.previous_amount != null ? ` ${r.previous_amount}` : ''}
                {r.previous_hours == null && r.previous_amount == null ? '—' : ''}
              </span>
            ),
          },
          {
            key: 'current',
            label: 'This cycle',
            minWidth: 130,
            render: (r) => (
              <span className="tabular-nums text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {r.current_hours != null ? `${r.current_hours} h` : ''}
                {r.current_amount != null ? ` ${r.current_amount}` : ''}
                {r.current_hours == null && r.current_amount == null ? '—' : ''}
              </span>
            ),
          },
          {
            key: 'change',
            label: 'Change',
            sortable: true,
            minWidth: 120,
            render: (r) => {
              const label =
                r.change === 'rolled_over'
                  ? 'Rolled over'
                  : r.change === 'resized'
                    ? 'Resized'
                    : r.change === 'added'
                      ? 'Added'
                      : 'Dropped';
              const tone =
                r.change === 'rolled_over'
                  ? 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
                  : 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--surface-raised)] dark:text-[var(--mustard)]';
              return (
                <span className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${tone}`}>
                  {label}
                </span>
              );
            },
          },
        ]}
        emptyTitle="No basis for a reallocation readout yet"
        emptyDescription="This readout compares declared claims against the previous cycle. It renders once this cycle carries forward from a named predecessor and both cycles have claims — until then there is nothing honest to show."
        contextBar={{
          label: loadError ?? `Plan cycle · ${realloc.length} movement${realloc.length !== 1 ? 's' : ''}`,
        }}
        shellClassName="h-full"
      />
    );
  }

  if (view === 'claims') {
    return (
      <>
        <EntityListPage<ResourceClaimFollowThroughRow, string>
          items={claims}
          loading={loading}
          getId={(c) => c.id ?? ''}
          masterDetail={false}
          title="Terms & Claims"
          subtitle="Declared resources per commitment — and whether allocation followed"
          icon={<ClipboardCheck className="h-4 w-4 text-primary-600" />}
          iconBgClassName="bg-primary-50"
          itemLabel="claim"
          searchFields={['basis', 'resource_type']}
          searchPlaceholder="Search claims…"
          sortOptions={CLAIM_SORT_OPTIONS}
          defaultSort={{ field: 'created_at', direction: 'desc' }}
          viewModes={['table']}
          defaultViewMode="table"
          selectable={false}
          primaryAction={{
            label: 'Declare claim',
            onClick: () => setModal({ kind: 'declare' }),
            disabled: currentItems.length === 0,
          }}
          headerActions={viewToggle}
          tableColumns={[
            {
              key: 'item',
              label: 'Commitment',
              minWidth: 200,
              render: (c) => (
                <CommitmentCell
                  planCycleId={ctx.cycleId}
                  itemId={c.item_id}
                  label={itemLabel(c.item_id, itemTitles)}
                  linkEnabled={commitmentViewOn}
                  quotedVersionNumber={
                    c.declared_in_plan_version_id
                      ? (versionNumbers.get(c.declared_in_plan_version_id) ?? null)
                      : null
                  }
                  authoritativeVersionNumber={authorizedVersion}
                />
              ),
            },
            {
              key: 'declared',
              label: 'Declared',
              minWidth: 120,
              render: (c) => (
                <span className="tabular-nums text-sm">
                  {c.claim_kind === 'capacity_hours'
                    ? `${c.declared_hours ?? '—'} h`
                    : `${c.currency ?? ''} ${c.declared_amount ?? '—'}`}
                  {c.resource_type ? (
                    <span className="ml-1 text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {c.resource_type}
                    </span>
                  ) : null}
                </span>
              ),
            },
            {
              key: 'live',
              label: 'Live',
              minWidth: 100,
              render: (c) => (
                <span className="tabular-nums text-sm">
                  {c.claim_kind === 'capacity_hours'
                    ? c.allocated_hours_live != null
                      ? `${c.allocated_hours_live} h`
                      : '—'
                    : c.envelope_approved_total != null
                      ? `${c.currency ?? ''} ${c.envelope_approved_total}`
                      : '—'}
                </span>
              ),
            },
            {
              key: 'followthrough',
              label: 'Follow-through',
              minWidth: 220,
              render: (c) => {
                if (c.status !== 'declared') {
                  return (
                    <span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                      {c.status === 'superseded' ? 'Superseded' : 'Withdrawn'}
                    </span>
                  );
                }
                const verdict = judgeFollowThrough({
                  basisState: c.basis_state,
                  claimKind: (c.claim_kind ?? 'capacity_hours') as 'capacity_hours' | 'funding',
                  declaredHours: c.declared_hours,
                  allocatedHoursLive: c.allocated_hours_live,
                  declaredAmount: c.declared_amount,
                  envelopeApprovedTotal: c.envelope_approved_total,
                });
                const tone =
                  verdict.state === 'under' || verdict.state === 'over'
                    ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'
                    : verdict.state === 'matched'
                      ? 'text-primary-700 dark:text-[var(--text-sec)]'
                      : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]';
                return <span className={`text-xs ${tone}`}>{verdict.sentence}</span>;
              },
            },
            {
              key: 'actions',
              label: '',
              minWidth: 140,
              render: (c) =>
                c.status === 'declared' ? (
                  <span className="flex gap-2">
                    <button
                      className="text-xs text-primary-600 hover:underline"
                      onClick={() => setModal({ kind: 'declare', supersedes: c })}
                    >
                      Supersede
                    </button>
                    <button
                      className="text-xs text-[var(--text-ter)] hover:underline dark:text-[var(--text-ter)]"
                      onClick={() => setModal({ kind: 'withdraw-claim', claim: c })}
                    >
                      Withdraw
                    </button>
                  </span>
                ) : null,
            },
          ]}
          afterToolbar={
            <div className="px-4 pb-2">
              <Link to="/planning/engine/funding" className="text-xs text-primary-600 hover:underline">
                Funding envelopes are edited in Funding &amp; Costs →
              </Link>
            </div>
          }
          emptyTitle="No resource claims yet"
          emptyDescription="Declare what each commitment gets — hours or funding — so the record can show whether allocation followed the decision."
          emptyAction={
            currentItems.length > 0
              ? { label: 'Declare claim', onClick: () => setModal({ kind: 'declare' }) }
              : undefined
          }
          contextBar={{ label: loadError ?? `Plan cycle · ${claims.length} claim${claims.length !== 1 ? 's' : ''}` }}
          shellClassName="h-full"
        />
        {modal?.kind === 'declare' && (
          <DeclareClaimModal
            cycleId={ctx.cycleId}
            items={currentItems}
            supersedes={modal.supersedes}
            onClose={() => setModal(null)}
            onDone={closeAndReload}
          />
        )}
        {modal?.kind === 'withdraw-claim' && (
          <WithdrawClaimModal
            claim={modal.claim}
            onClose={() => setModal(null)}
            onDone={closeAndReload}
          />
        )}
      </>
    );
  }

  return (
    <>
      <EntityListPage<CommitmentTermCheckRow, string>
        items={terms}
        loading={loading}
        getId={(t) => t.id ?? ''}
        masterDetail={false}
        title="Terms & Claims"
        subtitle="Stop conditions on this plan's commitments — attached at agreement, decided by ceremony"
        icon={<ClipboardCheck className="h-4 w-4 text-primary-600" />}
        iconBgClassName="bg-primary-50"
        itemLabel="term"
        searchFields={['condition_text']}
        searchPlaceholder="Search terms…"
        sortOptions={TERM_SORT_OPTIONS}
        defaultSort={{ field: 'review_by', direction: 'asc' }}
        viewModes={['table']}
        defaultViewMode="table"
        selectable={false}
        primaryAction={{
          label: 'Attach term',
          onClick: () => setModal({ kind: 'attach' }),
          disabled: currentItems.length === 0,
        }}
        headerActions={viewToggle}
        tableColumns={[
          {
            key: 'item',
            label: 'Commitment',
            minWidth: 200,
            render: (t) => (
              <CommitmentCell
                planCycleId={ctx.cycleId}
                itemId={t.item_id}
                label={itemLabel(t.item_id, itemTitles)}
                linkEnabled={commitmentViewOn}
                quotedVersionNumber={
                  t.created_in_plan_version_id
                    ? (versionNumbers.get(t.created_in_plan_version_id) ?? null)
                    : null
                }
                authoritativeVersionNumber={authorizedVersion}
              />
            ),
          },
          {
            key: 'kind',
            label: 'Kind',
            minWidth: 110,
            render: (t) => (
              <span className="text-xs text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {TERM_KIND_LABELS[(t.kind ?? 'review_trigger') as TermKind]}
              </span>
            ),
          },
          {
            key: 'condition_text',
            label: 'Condition',
            minWidth: 260,
            render: (t) => (
              <span className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                {t.condition_text}
              </span>
            ),
          },
          {
            key: 'review_by',
            label: 'Review by',
            minWidth: 100,
            render: (t) => (
              <span
                className={`tabular-nums text-sm ${
                  t.is_overdue
                    ? 'font-semibold text-[var(--mustard)] dark:text-[var(--mustard)]'
                    : 'text-[var(--text-sec)] dark:text-[var(--text-sec)]'
                }`}
              >
                {t.review_by ?? '—'}
              </span>
            ),
          },
          {
            key: 'status',
            label: 'Status',
            minWidth: 130,
            render: (t) => (
              <span className="flex flex-col gap-0.5">
                <TermStatusChip term={t} today={today} />
                {t.status === 'tripped' && t.id && trippedSignals.has(t.id) && (
                  <Link
                    to={`/planning/plans/${ctx.cycleId}`}
                    className="text-[11px] text-primary-600 hover:underline dark:text-[var(--text-sec)]"
                  >
                    Signal recorded — view it
                  </Link>
                )}
              </span>
            ),
          },
          {
            key: 'actions',
            label: '',
            minWidth: 180,
            render: (t) => {
              const status = (t.status ?? '') as 'armed' | 'tripped' | 'resolved' | 'withdrawn';
              if (!isTermOpen(status)) return null;
              return (
                <span className="flex gap-2">
                  {status === 'armed' && (
                    <button
                      className="text-xs text-[var(--mustard)] hover:underline dark:text-[var(--mustard)]"
                      onClick={() => setModal({ kind: 'term-action', term: t, action: 'trip' })}
                    >
                      Trip
                    </button>
                  )}
                  <button
                    className="text-xs text-primary-600 hover:underline"
                    onClick={() => setModal({ kind: 'term-action', term: t, action: 'resolve' })}
                  >
                    Resolve
                  </button>
                  {status === 'armed' && (
                    <button
                      className="text-xs text-[var(--text-ter)] hover:underline dark:text-[var(--text-ter)]"
                      onClick={() => setModal({ kind: 'term-action', term: t, action: 'withdraw' })}
                    >
                      Withdraw
                    </button>
                  )}
                </span>
              );
            },
          },
        ]}
        emptyTitle="No terms on this plan yet"
        emptyDescription="Attach kill criteria, review triggers, or expiry dates to this plan's commitments. Approval without expiry is accumulation by default."
        emptyAction={
          currentItems.length > 0
            ? { label: 'Attach term', onClick: () => setModal({ kind: 'attach' }) }
            : undefined
        }
        contextBar={{ label: loadError ?? `Plan cycle · ${terms.length} term${terms.length !== 1 ? 's' : ''}` }}
        shellClassName="h-full"
      />
      {modal?.kind === 'attach' && (
        <AttachTermModal
          cycleId={ctx.cycleId}
          items={currentItems}
          onClose={() => setModal(null)}
          onDone={closeAndReload}
        />
      )}
      {modal?.kind === 'term-action' && (
        <TermActionModal
          term={modal.term}
          action={modal.action}
          planCycleId={ctx.cycleId}
          onClose={() => setModal(null)}
          onDone={closeAndReload}
        />
      )}
    </>
  );
}

export default function PlanningTermsClaims() {
  const { cycleId: cycleIdFromParams } = useParams<{ cycleId?: string }>();
  const [selectedCycleId, setSelectedCycleId] = useState<string | null>(null);

  return (
    <div className="h-full min-h-[520px]">
      <PlanningCycleScope
        cycleIdParam={cycleIdFromParams}
        selectedCycleId={selectedCycleId}
        onSelectCycle={(id) => setSelectedCycleId(id)}
      >
        {(ctx) => <TermsClaimsContent ctx={ctx} />}
      </PlanningCycleScope>
    </div>
  );
}
